<template lang="pug">
  .items-shipped
    .items-shipped-header
      h3.rss-block-header {{ __('Items') }}
    .legend
      Row.header
        Column.product(
          mobile="7"
          :no-gutter="true"
        )
          span.font-weight-bold {{ __('Product') }} &nbsp;
          span ({{currentPackageItems.length}} {{ __(currentPackageItems.length > 1 ? 'items' : 'item') }})
        Column.sku(
          mobile="3"
          :no-gutter="true"
        ) #[span.font-weight-bold {{ __('Sku') }}]
        Column.quantity(
          mobile="2"
          :no-gutter="true"
        ) #[span.font-weight-bold {{ __('Qty') }}]
    .items-list(ref="itemsList")
      Row.item(
        v-for="(item, index) in currentPackageItems"
        :key="item.sku"
        v-show="!(index > 4 && !showAll)"
      )
        Column.product(
          mobile="7"
          :no-gutter="true"
        )
          RogueLink.item-link(v-if="itemUrl(item)" :to="itemUrl(item)")
            span.font-size-xxs {{ __(item.name) }}
          span.font-size-xxs(v-else) {{ __(item.name) }}
        Column.sku(
          mobile="3"
          :no-gutter="true"
        )
          span.font-size-xxs {{ item.sku }}
        Column.quantity(
          mobile="2"
          :no-gutter="true"
        )
          span.font-size-xxs {{ item.quantity || item.qty }}
    .btn-container(v-if="!showAll && currentPackageItems.length > 5")
      RogueButton(
        size="s"
        @click="showAll = true"
      ) {{ __('View All Items') }}
    .btn-container(v-if="hasInstructions")
      RogueButton(@click="showInstructions = true" size="s" color="black")
        | {{ __('View Instructions') }}
    OrderInstructionsDrawer(v-if="showInstructions" @hide="showInstructions = false" :order="currentPackage")
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import OrderInstructionsDrawer from '../../account/components/OrderInstructionsDrawer.vue'
import type { IRssPackage, IRssPackageItem } from '../rssTypes'
import { getBuyRequestMeta, listProductIds } from '@/js/helpers/buy-request'
import { useRogue } from '@/js/services/Rogue'
import { useProductStore } from '@/js/stores/productStore'
import type { BuyRequest } from '@/js/services/RogueTypes'
import { resolveSlingerXVisibleItems } from '@/js/pages/product/rackbuilder/products/rack/functionalTrainer/FM/slinger-x/slingerXUtils'

const props = defineProps<{
  currentPackage: Partial<IRssPackage>,
}>()

const productStore = useProductStore()
const rogue = useRogue()

const showAll = ref<boolean>(false)
const hasInstructions = ref<boolean>(false)
const showInstructions = ref<boolean>(false)

const currentPackageItems = computed<IRssPackageItem[]>(() => {
  const items = props.currentPackage.items

  return items ? resolveSlingerXVisibleItems(rogue, items as unknown as (BuyRequest & { [key: string]: any })[]) as IRssPackageItem[] : []
})

function itemUrl(item: IRssPackageItem) {
  try {
    return getBuyRequestMeta(item as BuyRequest, rogue).linkPath
  } catch (e) {
    return undefined
  }
}

onMounted(async () => {
  const productIds = listProductIds((props.currentPackage.items || []) as BuyRequest[])
  await productStore.loadAll(productIds, rogue)
  hasInstructions.value = productIds
    .map(id => productStore.products[id])
    // @ts-expect-error instructions property isn't defined on type
    .some(p => p.instructions?.some(i => /.+CUST_INSTR.+\.pdf/.test(i.Name)))
})
</script>

<style lang="scss" scoped>
  .items-shipped {
    background-color : $color-white;
    padding          : $space-s;
    height           : 100%;
  }

  .items-shipped-header {
    margin-bottom : $space-xxs;
  }

  .rss-block-header {
    margin-bottom  : 0;
    font-size      : $size-m;
    padding-bottom : $space-xxs;
    border-bottom  : $border-width-thin solid $color-gray-light;
    text-align     : center;

    @media(min-width: $desktop) {
      text-align: initial;
    }
  }

  .legend, .items-list {
    padding: 0 $space-xxs;
  }

  .legend {
    font-size : $size-xxs;
  }

  .legend .header, .items-list .item {
    padding       : $space-xxs 0;
    margin-bottom : $space-xxxs;
  }

  .quantity {
    text-align: center;
  }

  .items-list {
    .item {
      &:last-child {
        margin-bottom : 0;
      }

      .paragraph {
        margin-bottom : 0;
        font-size     : $size-xxs;
      }

      .product {
        font-weight : bold;

        .item-link{
          color: $color-black;
          text-decoration: none;
        }
      }

      .sku {
        color : $color-gray-dark;
      }
    }
  }

  .btn-container {
    margin-top : $space-xs;
  }
</style>
